<template>
  <div class="page" v-loading="loading">
    <div class="toolbar">
      <el-input
        v-model="keyword"
        placeholder="搜索量表名称"
        clearable
        class="search"
        @clear="fetchScales"
        @keyup.enter.native="fetchScales"
      />
      <el-select v-model="statusFilter" size="small" class="status-filter">
        <el-option label="全部" value="all" />
        <el-option label="仅启用" value="active" />
        <el-option label="仅未启用" value="inactive" />
      </el-select>
      <el-switch v-model="activeFirst" active-text="启用优先" />
      <div style="display: inline-block;">
        <el-button v-if="canEdit" type="primary" @click="openCreate">新建量表</el-button>
        <el-button v-if="canImport" type="success" @click="openImport">导入量表</el-button>
      </div>
    </div>

    <el-row :gutter="12">
      <el-col :span="8">
        <div class="card">
          <div class="card-title">量表列表</div>
          <el-table class="scale-list-table" :data="scales" height="520" highlight-current-row :row-class-name="getScaleRowClassName" @current-change="onSelectScale">
            <el-table-column prop="title" label="名称" min-width="200" />
            <el-table-column label="启用" width="80">
              <template slot-scope="{ row }">
                <el-tag :type="row.is_active ? 'success' : 'info'" size="mini" effect="plain">
                  {{ row.is_active ? '启用' : '未启用' }}
                </el-tag>
              </template>
            </el-table-column>
          </el-table>
          <div class="pager">
            <el-pagination
              layout="prev, pager, next"
              :page-size="pageSize"
              :current-page="page"
              :total="total"
              small
              @current-change="onPageChange"
            />
          </div>
        </div>
      </el-col>

      <el-col :span="16">
        <div class="card" v-if="currentScale">
          <div class="card-title">量表编辑</div>

          <el-tabs v-model="activeTab">
            <el-tab-pane label="基本信息" name="base">
              <el-form :model="editScale" label-width="90px">
                <el-form-item label="名称">
                  <el-input v-model="editScale.title" :disabled="readOnly" />
                </el-form-item>
                <el-form-item label="简介">
                  <el-input v-model="editScale.description" type="textarea" :rows="2" :disabled="readOnly" />
                </el-form-item>
                <el-form-item label="指导语">
                  <el-input v-model="editScale.instructions" type="textarea" :rows="4" :disabled="readOnly" />
                </el-form-item>
                <el-form-item label="是否启用">
                  <el-switch v-model="editScale.is_active" :disabled="readOnly" />
                  <div class="hint">启用前请确保题目与选项配置完整，否则会被自动禁用。</div>
                </el-form-item>

                <div class="actions">
                  <el-button v-if="canEdit" type="primary" :loading="saving" @click="saveScale">保存</el-button>
                  <el-button v-if="canExport" type="warning" plain @click="exportScale">导出JSON</el-button>
                  <el-button v-if="canDelete" type="danger" plain :loading="deleting" @click="deleteScale">删除</el-button>
                </div>
              </el-form>
            </el-tab-pane>

            <el-tab-pane label="题目" name="questions">
              <div class="sub-toolbar">
                <el-button v-if="canEdit" type="primary" @click="openQuestionDialog()">新增题目</el-button>
              </div>
              <el-table :data="questions" stripe height="460" ref="questionsTable" row-key="id">
                <el-table-column width="50" align="center">
                  <template slot-scope="{}">
                    <i v-if="canEdit" class="el-icon-rank drag-handle" style="cursor: move; font-size: 18px; color: #909399;"></i>
                  </template>
                </el-table-column>
                <el-table-column prop="order" label="序号" width="60" />
                <el-table-column prop="code" label="代码" width="100" />
                <el-table-column prop="content" label="题目" min-width="260" />
                <el-table-column label="题型" width="120">
                  <template slot-scope="{ row }">{{ widgetTypeLabel(row.widget_type) }}</template>
                </el-table-column>
                <el-table-column label="反分" width="60" align="center">
                  <template slot-scope="{ row }">
                    <i v-if="row.is_reverse_score" class="el-icon-check" style="color: #67C23A; font-weight: bold;"></i>
                  </template>
                </el-table-column>
                <el-table-column label="操作" width="180" align="center">
                  <template slot-scope="{ row }">
                    <el-tooltip v-if="canEdit" content="复制题目" placement="top" :open-delay="500">
                      <el-button size="mini" circle icon="el-icon-document-copy" @click="duplicateQuestion(row)"></el-button>
                    </el-tooltip>
                    <el-tooltip content="编辑题目" placement="top" :open-delay="500">
                      <el-button type="primary" size="mini" circle icon="el-icon-edit" @click="openQuestionDialog(row)"></el-button>
                    </el-tooltip>
                    <el-tooltip v-if="canEdit" content="删除题目" placement="top" :open-delay="500">
                      <el-button type="danger" size="mini" circle icon="el-icon-delete" @click="deleteQuestion(row)"></el-button>
                    </el-tooltip>
                  </template>
                </el-table-column>
              </el-table>
            </el-tab-pane>

            <el-tab-pane label="维度" name="dimensions">
              <div class="sub-toolbar">
                <el-button v-if="canEdit" type="primary" @click="openDimensionDialog()">新增维度</el-button>
              </div>
              <el-table :data="dimensions" stripe height="460" ref="dimensionsTable" row-key="id">
                <el-table-column width="50" align="center">
                  <template slot-scope="{}">
                    <i v-if="canEdit" class="el-icon-rank drag-handle" style="cursor: move; font-size: 18px; color: #909399;"></i>
                  </template>
                </el-table-column>
                <el-table-column prop="order" label="排序" width="80" />
                <el-table-column prop="name" label="名称" min-width="180" />
                <el-table-column prop="code" label="代码" width="140" />
                <el-table-column label="方式" width="160">
                  <template slot-scope="{ row }">{{ methodLabel(row.method) }}</template>
                </el-table-column>
                <el-table-column label="操作" width="150" align="center">
                  <template slot-scope="{ row }">
                    <el-tooltip content="编辑维度" placement="top" :open-delay="500">
                      <el-button type="primary" size="mini" circle icon="el-icon-edit" @click="openDimensionDialog(row)"></el-button>
                    </el-tooltip>
                    <el-tooltip v-if="canEdit" content="删除维度" placement="top" :open-delay="500">
                      <el-button type="danger" size="mini" circle icon="el-icon-delete" @click="deleteDimension(row)"></el-button>
                    </el-tooltip>
                  </template>
                </el-table-column>
              </el-table>
            </el-tab-pane>

            <el-tab-pane label="解释" name="interpretations">
              <div class="sub-toolbar">
                <el-button v-if="canEdit" type="primary" @click="openInterpretationDialog()">新增规则</el-button>
              </div>
              <el-table :data="interpretations" stripe height="460">
                <el-table-column label="维度" min-width="160">
                  <template slot-scope="{ row }">{{ dimensionName(row.dimension) }}</template>
                </el-table-column>
                <el-table-column prop="min_score" label="最小" width="90" />
                <el-table-column prop="max_score" label="最大" width="90" />
                <el-table-column prop="result_text" label="结论" min-width="220" />
                <el-table-column label="操作" width="150" align="center">
                  <template slot-scope="{ row }">
                    <el-tooltip content="编辑规则" placement="top" :open-delay="500">
                      <el-button type="primary" size="mini" circle icon="el-icon-edit" @click="openInterpretationDialog(row)"></el-button>
                    </el-tooltip>
                    <el-tooltip v-if="canEdit" content="删除规则" placement="top" :open-delay="500">
                      <el-button type="danger" size="mini" circle icon="el-icon-delete" @click="deleteInterpretation(row)"></el-button>
                    </el-tooltip>
                  </template>
                </el-table-column>
              </el-table>
            </el-tab-pane>
          </el-tabs>
        </div>

        <div class="card empty" v-else>
          <div class="empty-title">请选择一个量表</div>
        </div>
      </el-col>
    </el-row>

    <el-dialog title="导入量表 (JSON)" :visible.sync="importDialogVisible" width="500px">
      <div style="text-align: center; margin-bottom: 20px;">
        <el-upload
          class="upload-demo"
          drag
          action=""
          :auto-upload="false"
          :on-change="onImportFileChange"
          :file-list="importFileList"
          accept=".json"
          :limit="1">
          <i class="el-icon-upload"></i>
          <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
          <div class="el-upload__tip" slot="tip">只能上传json文件</div>
        </el-upload>
      </div>
      <span slot="footer">
        <el-button @click="downloadTemplate" type="text" style="float: left;">下载JSON模板</el-button>
        <el-button @click="importDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="importing" @click="confirmImport">导入</el-button>
      </span>
    </el-dialog>

    <el-dialog :title="questionDialogTitle" :visible.sync="questionDialogVisible" width="760px">
      <el-form :model="questionForm" label-width="90px">
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="序号">
              <el-input-number v-model="questionForm.order" :min="1" :disabled="readOnly" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="代码">
              <el-input v-model="questionForm.code" :disabled="true" placeholder="系统自动生成 (Q+序号)" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="题型">
              <el-select v-model="questionForm.widget_type" :disabled="readOnly" style="width: 100%" @change="onQuestionWidgetTypeChange">
                <el-option label="单选" value="radio" />
                <el-option label="文本" value="text" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="反向记分">
              <el-switch v-model="questionForm.is_reverse_score" :disabled="readOnly" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="题目内容">
          <el-input v-model="questionForm.content" type="textarea" :rows="3" :disabled="readOnly" />
        </el-form-item>

        <div v-if="questionForm.widget_type === 'radio'" class="inline-section">
          <div class="inline-title">
            <div>选项</div>
            <el-button v-if="canEdit" size="mini" type="primary" @click="addQuestionOptionRow" :disabled="readOnly">新增选项</el-button>
          </div>
          <el-table :data="questionOptions" stripe height="240" v-loading="questionOptionsLoading">
            <el-table-column label="排序" width="90">
              <template slot-scope="{ row }">
                <el-input-number v-model="row.order" :min="1" :disabled="readOnly" style="width: 100%" :controls="false" />
              </template>
            </el-table-column>
            <el-table-column label="文本" min-width="220">
              <template slot-scope="{ row }">
                <el-input v-model="row.text" :disabled="readOnly" />
              </template>
            </el-table-column>
            <el-table-column label="分值" width="160">
              <template slot-scope="{ row }">
                <el-input-number v-model="row.value" :disabled="readOnly" style="width: 100%" />
              </template>
            </el-table-column>
            <el-table-column label="操作" width="120">
              <template slot-scope="{ row, $index }">
                <el-button v-if="canEdit" size="mini" type="danger" plain @click="removeQuestionOptionRow(row, $index)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
          <div class="hint">单选题必须至少配置 1 个选项。</div>
        </div>
      </el-form>
      <span slot="footer">
        <el-button @click="questionDialogVisible = false">取消</el-button>
        <el-button v-if="canEdit" type="primary" :loading="saving" @click="saveQuestion">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog :title="dimensionDialogTitle" :visible.sync="dimensionDialogVisible" width="860px">
      <el-form :model="dimensionForm" label-width="90px">
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="名称">
              <el-input v-model="dimensionForm.name" :disabled="readOnly" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="代码">
              <el-input v-model="dimensionForm.code" :disabled="readOnly" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="方式">
              <el-select v-model="dimensionForm.method" :disabled="readOnly" style="width: 100%" @change="onDimensionMethodChange">
                <el-option label="题目求和" value="SUM" />
                <el-option label="题目均分" value="AVG" />
                <el-option label="公式计算" value="FORMULA" />
                <el-option label="常模标准转换" value="NORM" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="排序">
              <el-input-number v-model="dimensionForm.order" :min="0" :disabled="readOnly" style="width: 100%" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="描述">
          <el-input v-model="dimensionForm.description" type="textarea" :rows="2" :disabled="readOnly" />
        </el-form-item>
        <el-form-item label="公式">
          <el-input v-model="dimensionForm.formula" :disabled="readOnly" placeholder="例如：{default_total} / 90" />
        </el-form-item>
        <el-form-item label="依赖维度">
          <el-select v-model="dimensionForm.depends_on" multiple filterable :disabled="readOnly" style="width: 100%">
            <el-option v-for="d in dimensions" :key="d.id" :label="`${d.name} (${d.code})`" :value="d.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="关联题目">
          <el-select v-model="dimensionForm.questions" multiple filterable :disabled="readOnly" style="width: 100%">
            <el-option v-for="q in questions" :key="q.id" :label="`${q.order}. ${q.content}`" :value="q.id" />
          </el-select>
          <div class="question-actions">
            <el-button v-if="canEdit" size="mini" :disabled="readOnly || questions.length === 0" @click="openQuestionSelector">选择题目</el-button>
            <el-button v-if="canEdit" size="mini" :disabled="readOnly || questions.length === 0" @click="selectAllDimensionQuestions">全选</el-button>
            <el-button v-if="canEdit" size="mini" :disabled="readOnly || (dimensionForm.questions || []).length === 0" @click="invertDimensionQuestions">反选</el-button>
            <el-button v-if="canEdit" size="mini" :disabled="readOnly || (dimensionForm.questions || []).length === 0" @click="clearDimensionQuestions">清空</el-button>
          </div>
        </el-form-item>

        <div v-if="dimensionForm.method === 'NORM'" class="inline-section">
          <div class="inline-title">
            <div>常模</div>
            <div class="inline-actions">
              <el-button v-if="canEdit" size="mini" type="primary" @click="openNormDialog()" :disabled="readOnly || !dimensionForm.id">
                新增常模
              </el-button>
            </div>
          </div>
          <div v-if="!dimensionForm.id" class="hint">请先保存维度后再添加常模。</div>
          <el-table v-else :data="dimensionNorms" stripe height="220" v-loading="dimensionNormsLoading">
            <el-table-column prop="name" label="名称" min-width="140" />
            <el-table-column label="性别" width="100">
              <template slot-scope="{ row }">{{ genderLabel(row.gender) }}</template>
            </el-table-column>
            <el-table-column prop="min_age" label="最小年龄" width="100" />
            <el-table-column prop="max_age" label="最大年龄" width="100" />
            <el-table-column prop="mean" label="均值" width="100" />
            <el-table-column prop="sd" label="标准差" width="100" />
            <el-table-column label="类型" width="120">
              <template slot-scope="{ row }">{{ normTypeLabel(row.norm_type) }}</template>
            </el-table-column>
            <el-table-column label="操作" width="150" align="center">
              <template slot-scope="{ row }">
                <el-tooltip content="编辑常模" placement="top" :open-delay="500">
                  <el-button size="mini" circle icon="el-icon-edit" @click="openNormDialog(row)"></el-button>
                </el-tooltip>
                <el-tooltip v-if="canEdit" content="删除常模" placement="top" :open-delay="500">
                  <el-button type="danger" size="mini" circle icon="el-icon-delete" @click="deleteNorm(row)"></el-button>
                </el-tooltip>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-form>
      <span slot="footer">
        <el-button @click="dimensionDialogVisible = false">取消</el-button>
        <el-button v-if="canEdit" type="primary" :loading="saving" @click="saveDimension">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog title="选择题目" :visible.sync="questionSelectorVisible" width="980px" custom-class="question-selector-dialog">
      <div class="question-selector__toolbar">
        <el-input v-model="questionSelectorQuery" clearable placeholder="搜索题号/题干" class="question-selector__search" />
        <div class="question-selector__meta">
          <span>已选 {{ questionSelectorSelectedIds.length }} / {{ questions.length }}</span>
          <span class="question-selector__hint">Shift 范围选择，Ctrl/⌘ 追加/取消</span>
        </div>
      </div>
      <el-table
        :data="questionSelectorFiltered"
        height="520"
        border
        size="mini"
        :row-key="(row) => row.id"
        :row-class-name="questionSelectorRowClass"
        @row-click="onQuestionSelectorRowClick"
      >
        <el-table-column label="题号" prop="order" width="70" />
        <el-table-column label="题干" min-width="560">
          <template slot-scope="{ row }">
            <span>{{ row.content }}</span>
          </template>
        </el-table-column>
        <el-table-column label="已选" width="70" align="center">
          <template slot-scope="{ row }">
            <span @click.stop>
              <el-checkbox :value="questionSelectorSelectedSet.has(row.id)" @change="() => toggleQuestionSelectorId(row.id)" />
            </span>
          </template>
        </el-table-column>
      </el-table>
      <span slot="footer">
        <el-button @click="cancelQuestionSelector">取消</el-button>
        <el-button type="primary" @click="confirmQuestionSelector">确定</el-button>
      </span>
    </el-dialog>

    <el-dialog :title="interpretationDialogTitle" :visible.sync="interpretationDialogVisible" width="820px">
      <el-form :model="interpretationForm" label-width="90px">
        <el-form-item label="维度">
          <el-select v-model="interpretationForm.dimension" clearable filterable :disabled="readOnly" style="width: 100%">
            <el-option v-for="d in dimensions" :key="d.id" :label="`${d.name} (${d.code})`" :value="d.id" />
          </el-select>
        </el-form-item>
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="最小分值">
              <el-input-number v-model="interpretationForm.min_score" :disabled="readOnly" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="最大分值">
              <el-input-number v-model="interpretationForm.max_score" :disabled="readOnly" style="width: 100%" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="结论">
          <el-input v-model="interpretationForm.result_text" type="textarea" :rows="3" :disabled="readOnly" />
        </el-form-item>
        <el-form-item label="建议">
          <el-input v-model="interpretationForm.suggestion" type="textarea" :rows="3" :disabled="readOnly" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="interpretationDialogVisible = false">取消</el-button>
        <el-button v-if="canEdit" type="primary" :loading="saving" @click="saveInterpretation">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog :title="normDialogTitle" :visible.sync="normDialogVisible" width="760px">
      <el-form :model="normForm" label-width="90px">
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="名称">
              <el-input v-model="normForm.name" :disabled="readOnly" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="性别">
              <el-select v-model="normForm.gender" :disabled="readOnly" style="width: 100%">
                <el-option label="通用" value="ALL" />
                <el-option label="男性" value="M" />
                <el-option label="女性" value="F" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="12">
          <el-col :span="8">
            <el-form-item label="最小年龄">
              <el-input-number v-model="normForm.min_age" :min="0" :disabled="readOnly" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="最大年龄">
              <el-input-number v-model="normForm.max_age" :min="0" :disabled="readOnly" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="类型">
              <el-select v-model="normForm.norm_type" :disabled="readOnly" style="width: 100%">
                <el-option label="T分" value="T_SCORE" />
                <el-option label="Z分" value="Z_SCORE" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="均值">
              <el-input-number v-model="normForm.mean" :disabled="readOnly" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="标准差">
              <el-input-number v-model="normForm.sd" :disabled="readOnly" style="width: 100%" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <span slot="footer">
        <el-button @click="normDialogVisible = false">取消</el-button>
        <el-button v-if="canEdit" type="primary" :loading="saving" @click="saveNorm">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import Sortable from 'sortablejs'
import {
  getAdminScales,
  createScale,
  updateScale,
  deleteScale,
  importScale,
  getQuestions,
  createQuestion,
  updateQuestion,
  reorderQuestions,
  deleteQuestion,
  getOptions,
  createOption,
  updateOption,
  deleteOption,
  getDimensions,
  createDimension,
  updateDimension,
  deleteDimension,
  reorderDimensions,
  getInterpretations,
  createInterpretation,
  updateInterpretation,
  deleteInterpretation,
  getNorms,
  createNorm,
  updateNorm,
  deleteNorm,
  exportScale
} from '@/api/scales'
import { notifyApiError } from '@/utils/notifyApiError'

export default {
  name: 'ScalesManage',
  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      keyword: '',
      statusFilter: 'all',
      activeFirst: true,
      page: 1,
      pageSize: 20,
      total: 0,

      activeTab: 'base',
      scales: [],
      currentScale: null,
      editScale: null,

      sortable: null,
      dimensionsSortable: null,
      questions: [],
      questionDialogVisible: false,
      questionForm: { id: null, scale: null, content: '', order: 1, widget_type: 'radio', is_reverse_score: false },
      questionOriginalWidgetType: 'radio',
      questionOptionsLoading: false,
      questionOptions: [],
      questionOptionDeletedIds: [],

      dimensions: [],
      dimensionDialogVisible: false,
      dimensionForm: { id: null, scale: null, name: '', code: '', description: '', method: 'SUM', depends_on: [], questions: [], formula: '', order: 0 },
      dimensionMethodBefore: 'SUM',
      dimensionNormsLoading: false,
      dimensionNorms: [],
      questionSelectorVisible: false,
      questionSelectorQuery: '',
      questionSelectorSelectedIds: [],
      questionSelectorSnapshot: [],
      questionSelectorAnchorIndex: null,

      interpretations: [],
      interpretationDialogVisible: false,
      interpretationForm: { id: null, scale: null, dimension: null, min_score: 0, max_score: 0, result_text: '', suggestion: '' },

      normDialogVisible: false,
      normForm: { id: null, dimension: null, name: '默认常模', gender: 'ALL', min_age: 0, max_age: 100, mean: 0, sd: 1, norm_type: 'T_SCORE' },

      importDialogVisible: false,
      importing: false,
      importFileList: []
    }
  },
  computed: {
    me() {
      return this.$store.state.me || {}
    },
    isSuperuser() {
      return !!this.me.is_superuser
    },
    isAdmin() {
      return this.me.role === 'admin'
    },
    canEdit() {
      if (this.isSuperuser) return true
      if (this.isAdmin) {
        return this.$store.state.settings.admin_can_edit_scales === 'True'
      }
      return false
    },
    canDelete() {
      if (this.isSuperuser) return true
      if (this.isAdmin) {
        return this.$store.state.settings.admin_can_delete_scales === 'True'
      }
      return false
    },
    canExport() {
      if (this.isSuperuser) return true
      if (this.isAdmin) {
        return this.$store.state.settings.admin_can_export_scales === 'True'
      }
      return false
    },
    canImport() {
      if (this.isSuperuser) return true
      if (this.isAdmin) {
        return this.$store.state.settings.admin_can_import_scales === 'True'
      }
      return false
    },
    readOnly() {
      return !this.canEdit
    },
    questionDialogTitle() {
      return this.questionForm && this.questionForm.id ? '编辑题目' : '新增题目'
    },
    dimensionDialogTitle() {
      return this.dimensionForm && this.dimensionForm.id ? '编辑维度' : '新增维度'
    },
    interpretationDialogTitle() {
      return this.interpretationForm && this.interpretationForm.id ? '编辑解释规则' : '新增解释规则'
    },
    normDialogTitle() {
      return this.normForm && this.normForm.id ? '编辑常模' : '新增常模'
    },
    questionSelectorSelectedSet() {
      return new Set(this.questionSelectorSelectedIds || [])
    },
    questionSelectorFiltered() {
      const q = (this.questionSelectorQuery || '').trim().toLowerCase()
      const list = (this.questions || []).slice().sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0))
      if (!q) return list
      return list.filter(item => {
        const orderText = String(item.order || '')
        const contentText = String(item.content || '').toLowerCase()
        return orderText.includes(q) || contentText.includes(q)
      })
    },
  },
  watch: {
    statusFilter() {
      this.page = 1
      this.fetchScales()
    },
    activeFirst() {
      this.page = 1
      this.fetchScales()
    },
    activeTab(val) {
      if (val === 'questions') {
        this.$nextTick(() => {
          this.initSortable()
        })
      }
      if (val === 'dimensions') {
        this.$nextTick(() => {
          this.initDimensionSortable()
        })
      }
    }
  },
  created() {
    this.fetchScales()
  },
  methods: {
    getScaleRowClassName({ row }) {
      if (row && row.is_active === false) return 'scale-row--inactive'
      return ''
    },
    widgetTypeLabel(v) {
      if (v === 'radio') return '单选'
      if (v === 'text') return '文本'
      return v || ''
    },
    methodLabel(v) {
      if (v === 'SUM') return '题目求和'
      if (v === 'AVG') return '题目均分'
      if (v === 'FORMULA') return '公式计算'
      if (v === 'NORM') return '常模标准转换'
      return v || ''
    },
    genderLabel(v) {
      if (v === 'ALL') return '通用'
      if (v === 'M') return '男性'
      if (v === 'F') return '女性'
      return v || ''
    },
    normTypeLabel(v) {
      if (v === 'T_SCORE') return 'T分'
      if (v === 'Z_SCORE') return 'Z分'
      return v || ''
    },
    async fetchAllPages(apiFunc, params) {
      const pageSize = 50
      let page = 1
      let items = []
      while (page <= 200) {
        const resp = await apiFunc({ ...(params || {}), page, page_size: pageSize })
        const data = resp.data || {}
        const chunk = Array.isArray(data) ? data : data.results || []
        items = items.concat(chunk)
        if (Array.isArray(data)) break
        if (!data.next || chunk.length === 0) break
        page += 1
      }
      return items
    },

    async fetchScales() {
      this.loading = true
      try {
        const params = { page: this.page, page_size: this.pageSize }
        if (this.keyword) params.search = this.keyword
        if (this.statusFilter === 'active') params.is_active = true
        if (this.statusFilter === 'inactive') params.is_active = false
        params.ordering = this.activeFirst ? '-is_active,-updated_at' : '-updated_at'
        const resp = await getAdminScales(params)
        this.scales = resp.data.results || []
        this.total = resp.data.count || 0
      } catch (e) {
        notifyApiError(e, '获取量表列表失败')
        this.scales = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    onPageChange(p) {
      this.page = p
      this.fetchScales()
    },
    async onSelectScale(row) {
      if (!row) return
      this.currentScale = row
      this.editScale = { ...row }
      await Promise.all([this.fetchQuestions(), this.fetchDimensions(), this.fetchInterpretations()])
    },
    openCreate() {
      this.currentScale = { id: null }
      this.editScale = { title: '', description: '', instructions: '', is_active: false }
      this.questions = []
      this.dimensions = []
      this.interpretations = []
      this.activeTab = 'base'
    },
    async saveScale() {
      if (!this.editScale.title) return this.$message.error('请输入名称')
      this.saving = true
      try {
        if (this.currentScale.id) {
          await updateScale(this.currentScale.id, this.editScale)
          this.$message.success('保存成功')
          await this.fetchScales()
          // Update current scale reference in list
          const found = this.scales.find(s => s.id === this.currentScale.id)
          if (found) this.onSelectScale(found)
        } else {
          const resp = await createScale(this.editScale)
          this.$message.success('创建成功')
          await this.fetchScales()
          const found = this.scales.find(s => s.id === resp.data.id)
          if (found) this.onSelectScale(found)
        }
      } catch (e) {
        notifyApiError(e, '保存失败')
      } finally {
        this.saving = false
      }
    },
    async exportScale() {
      if (!this.currentScale || !this.currentScale.id) return
      try {
        const resp = await exportScale(this.currentScale.id)
        const data = resp.data
        const jsonStr = JSON.stringify(data, null, 2)
        const blob = new Blob([jsonStr], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${data.title || 'scale'}.json`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
        this.$message.success('导出成功')
      } catch (e) {
        console.error(e)
        notifyApiError(e, '导出失败')
      }
    },
    async deleteScale() {
      if (!this.currentScale || !this.currentScale.id) return
      this.$confirm('确认删除该量表？', '提示', { type: 'warning' })
        .then(async () => {
          this.deleting = true
          try {
            await deleteScale(this.currentScale.id)
            this.$message.success('删除成功')
            this.currentScale = null
            this.editScale = null
            await this.fetchScales()
          } catch (e) {
            notifyApiError(e, '删除失败')
          } finally {
            this.deleting = false
          }
        })
        .catch(() => {})
    },

    // Import
    openImport() {
      this.importDialogVisible = true
      this.importFileList = []
    },
    onImportFileChange(file, fileList) {
      this.importFileList = fileList.slice(-1)
    },
    async confirmImport() {
      if (!this.importFileList.length) {
        this.$message.warning('请选择文件')
        return
      }
      const file = this.importFileList[0].raw
      this.importing = true
      try {
        const resp = await importScale(file)
        this.$message.success(resp.data.detail || '导入成功')
        this.importDialogVisible = false
        this.fetchScales()
      } catch (e) {
        // Handle 409 Conflict
        if (e.response && e.response.status === 409) {
          const data = e.response.data
          const title = data.title || '该量表'
          const hasData = data.has_data
          
          let message = `检测到同名量表：${title}。`
          if (hasData) {
            message += '<br/><span style="color: #F56C6C; font-weight: bold;">原量表已有历史测评记录，为保护数据，"替换"操作将自动归档原量表。</span>'
          } else {
            message += '<br/>原量表无历史数据，"替换"将覆盖原结构。'
          }
          message += '<br/><br/>请选择操作：'

          this.$confirm(message, '导入冲突', {
            dangerouslyUseHTMLString: true,
            distinguishCancelAndClose: true,
            confirmButtonText: '替换原量表',
            cancelButtonText: '作为新量表导入 (重命名)',
            type: 'warning'
          }).then(async () => {
            // Option: Overwrite / Archive & Replace
            const strategy = hasData ? 'archive_and_create' : 'overwrite'
            try {
              const retryResp = await importScale(file, { strategy: strategy, overwrite: !hasData })
              this.$message.success(retryResp.data.detail || '导入成功')
              this.importDialogVisible = false
              this.fetchScales()
            } catch (retryErr) {
              notifyApiError(retryErr, '导入失败')
            }
          }).catch(async (action) => {
            if (action === 'cancel') {
              // Option: Rename
              this.$prompt('请输入新量表名称', '重命名', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                inputValue: title + ' (副本)',
                inputPattern: /\S/,
                inputErrorMessage: '名称不能为空'
              }).then(async ({ value }) => {
                try {
                  const renameResp = await importScale(file, { new_title: value })
                  this.$message.success(renameResp.data.detail || '导入成功')
                  this.importDialogVisible = false
                  this.fetchScales()
                } catch (renameErr) {
                  notifyApiError(renameErr, '导入失败')
                }
              }).catch(() => {})
            }
            // else: closed dialog, do nothing
          })
        } else {
          notifyApiError(e, '导入失败')
        }
      } finally {
        this.importing = false
      }
    },
    downloadTemplate() {
      const template = [
        {
          "title": "示例量表：焦虑与压力综合评测",
          "description": "包含反向计分、维度依赖和公式计算的高级示例。",
          "instructions": "请根据过去一周的实际情况回答。",
          "questions": [
            {
              "content": "我觉得比平时容易紧张 (焦虑)",
              "widget_type": "radio",
              "code": "Q1",
              "options": [
                {"text": "从不", "value": 0},
                {"text": "有时", "value": 1},
                {"text": "经常", "value": 2},
                {"text": "总是", "value": 3}
              ]
            },
            {
              "content": "我感到心情平静 (反向计分)",
              "widget_type": "radio",
              "code": "Q2",
              "is_reverse_score": true,
              "options": [
                {"text": "从不", "value": 0},
                {"text": "有时", "value": 1},
                {"text": "经常", "value": 2},
                {"text": "总是", "value": 3}
              ]
            },
            {
              "content": "我感到压力很大 (压力)",
              "widget_type": "radio",
              "code": "Q3",
              "options": [
                {"text": "从不", "value": 0},
                {"text": "有时", "value": 1},
                {"text": "经常", "value": 2},
                {"text": "总是", "value": 3}
              ]
            }
          ],
          "dimensions": [
            {
              "name": "焦虑分量表",
              "code": "ANX",
              "method": "SUM",
              "question_indices": [0, 1],
              "description": "计算Q1和Q2(反向)的总和"
            },
            {
              "name": "压力分量表",
              "code": "STR",
              "method": "SUM",
              "question_indices": [2],
              "description": "计算Q3的得分"
            },
            {
              "name": "综合指数 (公式计算)",
              "code": "INDEX",
              "method": "FORMULA",
              "depends_on": ["ANX", "STR"],
              "formula": "({ANX} + {STR}) / 2",
              "description": "焦虑和压力的平均分"
            }
          ],
          "interpretations": [
            {
              "dimension_code": "INDEX",
              "min_score": 0,
              "max_score": 1,
              "result_text": "状态良好"
            },
            {
              "dimension_code": "INDEX",
              "min_score": 1.1,
              "max_score": 3,
              "result_text": "需要注意"
            }
          ]
        }
      ]
      
      const blob = new Blob([JSON.stringify(template, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'scale_import_template.json'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },

    async fetchQuestions() {
      if (!this.currentScale || !this.currentScale.id) return
      this.questions = await this.fetchAllPages(getQuestions, { scale: this.currentScale.id, ordering: 'order' })
      this.$nextTick(() => {
        this.initSortable()
      })
    },
    initSortable() {
      const el = this.$refs.questionsTable && this.$refs.questionsTable.$el.querySelectorAll('.el-table__body-wrapper > table > tbody')[0]
      if (!el) return
      
      if (this.sortable) this.sortable.destroy()
      
      this.sortable = Sortable.create(el, {
        handle: '.drag-handle',
        animation: 150,
        ghostClass: 'sortable-ghost',
        onEnd: async ({ newIndex, oldIndex }) => {
          if (newIndex === oldIndex) return
          
          const targetRow = this.questions.splice(oldIndex, 1)[0]
          this.questions.splice(newIndex, 0, targetRow)
          
          // Re-index locally to avoid UI jump before server response
          this.questions.forEach((q, i) => q.order = i + 1)
          
          const ids = this.questions.map(q => q.id)
          try {
            await reorderQuestions(ids)
            this.$message.success('排序已更新')
          } catch (e) {
            notifyApiError(e, '排序失败')
            this.fetchQuestions() // Revert on error
          }
        }
      })
    },
    async openQuestionDialog(row) {
      if (!this.currentScale || !this.currentScale.id) {
        this.$message.warning('请先保存量表基本信息')
        return
      }
      this.questionOptionDeletedIds = []
      this.questionOptions = []
      this.questionOptionsLoading = false

      if (row) {
        this.questionForm = { ...row }
        this.questionOriginalWidgetType = row.widget_type
      } else {
        const nextOrder = (this.questions[this.questions.length - 1] && this.questions[this.questions.length - 1].order + 1) || 1
        this.questionForm = { id: null, scale: this.currentScale.id, content: '', order: nextOrder, code: '', widget_type: 'radio', is_reverse_score: false }
        this.questionOriginalWidgetType = 'radio'
      }

      this.questionDialogVisible = true

      if (this.questionForm.widget_type === 'radio') {
        if (this.questionForm.id) await this.loadQuestionOptions(this.questionForm.id)
        if (!this.questionForm.id && this.questionOptions.length === 0) {
          this.questionOptions = [
            { id: null, order: 1, text: '', value: 0 },
            { id: null, order: 2, text: '', value: 0 },
          ]
        }
      }
    },
    onQuestionWidgetTypeChange(v) {
      if (v === 'radio') {
        if (!this.questionOptions.length) {
          this.questionOptions = [
            { id: null, order: 1, text: '', value: 0 },
            { id: null, order: 2, text: '', value: 0 },
          ]
        }
        return
      }
      this.questionOptionDeletedIds = []
      this.questionOptions = []
    },
    async loadQuestionOptions(questionId) {
      this.questionOptionsLoading = true
      try {
        const items = await this.fetchAllPages(getOptions, { question: questionId, ordering: 'order' })
        this.questionOptions = (items || []).map((o) => ({ id: o.id, order: o.order, text: o.text, value: o.value }))
      } finally {
        this.questionOptionsLoading = false
      }
    },
    addQuestionOptionRow() {
      const nextOrder = (this.questionOptions[this.questionOptions.length - 1] && this.questionOptions[this.questionOptions.length - 1].order + 1) || 1
      this.questionOptions.push({ id: null, order: nextOrder, text: '', value: 0 })
    },
    removeQuestionOptionRow(row, index) {
      if (row && row.id) this.questionOptionDeletedIds.push(row.id)
      this.questionOptions.splice(index, 1)
    },
    async deleteAllOptionsForQuestion(questionId) {
      const existing = await this.fetchAllPages(getOptions, { question: questionId })
      for (const o of existing) {
        await deleteOption(o.id)
      }
    },
    async syncQuestionOptions(questionId) {
      if (!questionId) return
      if (!this.questionOptions || this.questionOptions.length === 0) {
        this.$message.error('单选题至少需要 1 个选项')
        throw new Error('missing options')
      }

      for (const id of this.questionOptionDeletedIds) {
        await deleteOption(id)
      }
      this.questionOptionDeletedIds = []

      for (const o of this.questionOptions) {
        const payload = { question: questionId, text: o.text, value: o.value, order: o.order }
        if (!payload.text) {
          this.$message.error('选项文本不能为空')
          throw new Error('empty option text')
        }
        if (o.id) {
          await updateOption(o.id, { ...payload, id: o.id })
        } else {
          const resp = await createOption(payload)
          o.id = resp.data.id
        }
      }
    },
    async saveQuestion() {
      this.saving = true
      try {
        let saved = null
        if (this.questionForm.id) {
          const resp = await updateQuestion(this.questionForm.id, this.questionForm)
          saved = resp.data
        } else {
          const resp = await createQuestion({ ...this.questionForm, scale: this.currentScale.id })
          saved = resp.data
          this.questionForm.id = saved.id
        }

        if (this.questionForm.widget_type === 'radio') {
          await this.syncQuestionOptions(saved.id)
        } else if (this.questionOriginalWidgetType === 'radio' && saved && saved.id) {
          await this.deleteAllOptionsForQuestion(saved.id)
        }

        this.$message.success('保存成功')
        this.questionDialogVisible = false
        await this.fetchQuestions()
      } catch (e) {
        notifyApiError(e, '保存失败')
      } finally {
        this.saving = false
      }
    },
    async deleteQuestion(row) {
      if (!row || !row.id) return
      try {
        await this.$confirm('确认删除该题目？', '提示', { type: 'warning' })
        await deleteQuestion(row.id)
        this.$message.success('已删除')
        await this.fetchQuestions()
      } catch (e) {
        if (e !== 'cancel') notifyApiError(e, '删除失败')
      }
    },

    async duplicateQuestion(row) {
      if (!row || !row.id) return
      this.loading = true
      try {
        const options = await this.fetchAllPages(getOptions, { question: row.id, ordering: 'order' })
        
        const maxOrder = this.questions.length > 0 ? Math.max(...this.questions.map(q => q.order)) : 0
        const newQuestionData = {
          scale: this.currentScale.id,
          content: row.content + ' (副本)',
          widget_type: row.widget_type,
          is_reverse_score: row.is_reverse_score,
          order: maxOrder + 1,
        }
        
        const resp = await createQuestion(newQuestionData)
        const newQ = resp.data
        
        for (const opt of options) {
          await createOption({
            question: newQ.id,
            text: opt.text,
            value: opt.value,
            order: opt.order
          })
        }
        
        this.$message.success('复制成功')
        await this.fetchQuestions()
      } catch (e) {
        notifyApiError(e, '复制失败')
      } finally {
        this.loading = false
      }
    },

    async fetchDimensions() {
      if (!this.currentScale || !this.currentScale.id) return
      this.dimensions = await this.fetchAllPages(getDimensions, { scale: this.currentScale.id, ordering: 'order' })
      this.$nextTick(() => {
        this.initDimensionSortable()
      })
    },
    initDimensionSortable() {
      const el = this.$refs.dimensionsTable && this.$refs.dimensionsTable.$el.querySelectorAll('.el-table__body-wrapper > table > tbody')[0]
      if (!el) return

      if (this.dimensionsSortable) this.dimensionsSortable.destroy()

      this.dimensionsSortable = Sortable.create(el, {
        handle: '.drag-handle',
        animation: 150,
        ghostClass: 'sortable-ghost',
        onEnd: async ({ newIndex, oldIndex }) => {
          if (newIndex === oldIndex) return

          const targetRow = this.dimensions.splice(oldIndex, 1)[0]
          this.dimensions.splice(newIndex, 0, targetRow)

          this.dimensions.forEach((d, i) => d.order = i + 1)

          const ids = this.dimensions.map(d => d.id)
          try {
            await reorderDimensions(ids)
            this.$message.success('排序已更新')
          } catch (e) {
            notifyApiError(e, '排序失败')
            this.fetchDimensions()
          }
        }
      })
    },
    async openDimensionDialog(row) {
      if (!this.currentScale || !this.currentScale.id) {
        this.$message.warning('请先保存量表基本信息')
        return
      }
      if (row) {
        this.dimensionForm = {
          id: row.id,
          scale: row.scale,
          name: row.name,
          code: row.code,
          description: row.description || '',
          method: row.method,
          depends_on: row.depends_on || [],
          questions: row.questions || [],
          formula: row.formula || '',
          order: row.order,
        }
      } else {
        const maxOrder = (this.dimensions || [])
          .filter(d => d && d.code !== 'default_total')
          .reduce((m, d) => Math.max(m, Number(d.order) || 0), 0)
        this.dimensionForm = { id: null, scale: this.currentScale.id, name: '', code: '', description: '', method: 'SUM', depends_on: [], questions: [], formula: '', order: maxOrder + 1 }
      }
      this.dimensionDialogVisible = true
      this.dimensionMethodBefore = this.dimensionForm.method

      this.dimensionNorms = []
      if (this.dimensionForm.method === 'NORM' && this.dimensionForm.id) {
        await this.fetchNormsForDimension(this.dimensionForm.id)
      }
    },
    allQuestionIds() {
      return (this.questions || []).map(q => q.id)
    },
    selectAllDimensionQuestions() {
      this.dimensionForm.questions = this.allQuestionIds()
    },
    clearDimensionQuestions() {
      this.dimensionForm.questions = []
    },
    invertDimensionQuestions() {
      const selected = new Set(this.dimensionForm.questions || [])
      this.dimensionForm.questions = this.allQuestionIds().filter(id => !selected.has(id))
    },
    openQuestionSelector() {
      this.questionSelectorSnapshot = [...(this.dimensionForm.questions || [])]
      this.questionSelectorSelectedIds = [...(this.dimensionForm.questions || [])]
      this.questionSelectorQuery = ''
      this.questionSelectorAnchorIndex = null
      this.questionSelectorVisible = true
    },
    cancelQuestionSelector() {
      this.questionSelectorSelectedIds = [...(this.questionSelectorSnapshot || [])]
      this.questionSelectorVisible = false
    },
    confirmQuestionSelector() {
      this.dimensionForm.questions = [...(this.questionSelectorSelectedIds || [])]
      this.questionSelectorVisible = false
    },
    toggleQuestionSelectorId(id) {
      const set = new Set(this.questionSelectorSelectedIds || [])
      if (set.has(id)) set.delete(id)
      else set.add(id)
      this.questionSelectorSelectedIds = Array.from(set)
    },
    questionSelectorRowClass({ row }) {
      return this.questionSelectorSelectedSet.has(row.id) ? 'question-selector__row--selected' : ''
    },
    onQuestionSelectorRowClick(row, column, event) {
      const list = this.questionSelectorFiltered || []
      const index = list.findIndex(q => q.id === row.id)
      const isToggle = !!(event && (event.ctrlKey || event.metaKey))
      const isRange = !!(event && event.shiftKey)

      if (index < 0) {
        this.toggleQuestionSelectorId(row.id)
        return
      }

      if (isRange && this.questionSelectorAnchorIndex !== null) {
        const start = Math.min(this.questionSelectorAnchorIndex, index)
        const end = Math.max(this.questionSelectorAnchorIndex, index)
        const rangeIds = list.slice(start, end + 1).map(q => q.id)
        const set = isToggle ? new Set(this.questionSelectorSelectedIds || []) : new Set()
        rangeIds.forEach(id => set.add(id))
        this.questionSelectorSelectedIds = Array.from(set)
      } else if (isToggle) {
        this.toggleQuestionSelectorId(row.id)
        this.questionSelectorAnchorIndex = index
        return
      } else {
        this.questionSelectorSelectedIds = [row.id]
      }

      this.questionSelectorAnchorIndex = index
    },
    async onDimensionMethodChange(v) {
      const prev = this.dimensionMethodBefore
      if (prev === 'NORM' && v !== 'NORM' && this.dimensionForm.id) {
        try {
          await this.$confirm('切换计算方式将删除该维度已有常模，是否继续？', '提示', { type: 'warning' })
        } catch (e) {
          this.dimensionForm.method = prev
          return
        }
      }

      this.dimensionMethodBefore = v
      if (v === 'NORM' && this.dimensionForm.id) {
        await this.fetchNormsForDimension(this.dimensionForm.id)
      }
      if (v !== 'NORM') {
        this.dimensionNorms = []
        this.normDialogVisible = false
      }
    },
    async fetchNormsForDimension(dimensionId) {
      this.dimensionNormsLoading = true
      try {
        this.dimensionNorms = await this.fetchAllPages(getNorms, { dimension: dimensionId, ordering: 'min_age' })
      } finally {
        this.dimensionNormsLoading = false
      }
    },
    async saveDimension() {
      this.saving = true
      try {
        let saved = null
        if (this.dimensionForm.id) {
          const resp = await updateDimension(this.dimensionForm.id, this.dimensionForm)
          saved = resp.data
        } else {
          const resp = await createDimension(this.dimensionForm)
          saved = resp.data
          this.dimensionForm.id = saved.id
        }

        this.$message.success('保存成功')
        this.dimensionDialogVisible = false
        await this.fetchDimensions()
      } catch (e) {
        notifyApiError(e, '保存失败')
      } finally {
        this.saving = false
      }
    },
    async deleteDimension(row) {
      try {
        await this.$confirm('确认删除该维度？', '提示', { type: 'warning' })
        await deleteDimension(row.id)
        this.$message.success('已删除')
        await this.fetchDimensions()
      } catch (e) {
        if (e !== 'cancel') notifyApiError(e, '删除失败')
      }
    },

    async fetchInterpretations() {
      if (!this.currentScale || !this.currentScale.id) return
      this.interpretations = await this.fetchAllPages(getInterpretations, { scale: this.currentScale.id })
    },
    dimensionName(dimensionId) {
      if (!dimensionId) return '总分'
      const d = this.dimensions.find((x) => x.id === dimensionId)
      return d ? d.name : String(dimensionId)
    },
    openInterpretationDialog(row) {
      if (!this.currentScale || !this.currentScale.id) {
        this.$message.warning('请先保存量表基本信息')
        return
      }
      if (row) {
        this.interpretationForm = { ...row }
      } else {
        this.interpretationForm = { id: null, scale: this.currentScale.id, dimension: null, min_score: 0, max_score: 0, result_text: '', suggestion: '' }
      }
      this.interpretationDialogVisible = true
    },
    async saveInterpretation() {
      this.saving = true
      try {
        if (this.interpretationForm.id) {
          await updateInterpretation(this.interpretationForm.id, this.interpretationForm)
        } else {
          await createInterpretation({ ...this.interpretationForm, scale: this.currentScale.id })
        }
        this.$message.success('保存成功')
        this.interpretationDialogVisible = false
        await this.fetchInterpretations()
      } catch (e) {
        notifyApiError(e, '保存失败')
      } finally {
        this.saving = false
      }
    },
    async deleteInterpretation(row) {
      try {
        await this.$confirm('确认删除该解释规则？', '提示', { type: 'warning' })
        await deleteInterpretation(row.id)
        this.$message.success('已删除')
        await this.fetchInterpretations()
      } catch (e) {
        if (e !== 'cancel') notifyApiError(e, '删除失败')
      }
    },

    openNormDialog(row) {
      if (!this.dimensionForm || !this.dimensionForm.id) {
        this.$message.warning('请先保存维度')
        return
      }
      if (row) {
        this.normForm = { ...row }
      } else {
        this.normForm = { id: null, dimension: this.dimensionForm.id, name: '默认常模', gender: 'ALL', min_age: 0, max_age: 100, mean: 0, sd: 1, norm_type: 'T_SCORE' }
      }
      this.normDialogVisible = true
    },
    async saveNorm() {
      this.saving = true
      try {
        if (this.normForm.min_age > this.normForm.max_age) {
          this.$message.error('最小年龄不能大于最大年龄')
          return
        }
        if (this.normForm.sd === 0) {
          this.$message.error('标准差不能为 0')
          return
        }
        if (this.normForm.id) {
          await updateNorm(this.normForm.id, this.normForm)
        } else {
          await createNorm(this.normForm)
        }
        this.$message.success('保存成功')
        this.normDialogVisible = false
        await this.fetchNormsForDimension(this.dimensionForm.id)
      } catch (e) {
        notifyApiError(e, '保存失败')
      } finally {
        this.saving = false
      }
    },
    async deleteNorm(row) {
      try {
        await this.$confirm('确认删除该常模？', '提示', { type: 'warning' })
        await deleteNorm(row.id)
        this.$message.success('已删除')
        await this.fetchNormsForDimension(this.dimensionForm.id)
      } catch (e) {
        if (e !== 'cancel') notifyApiError(e, '删除失败')
      }
    },
  },
}
</script>

<style scoped>
.toolbar {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 12px;
}
.search {
  max-width: 360px;
}
.status-filter {
  width: 120px;
}
.card {
  background: var(--bg-card);
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 8px 24px rgba(17, 24, 39, 0.06);
}
.card-title {
  font-weight: 800;
  color: var(--text-primary);
  margin-bottom: 10px;
}
.scale-list-table ::v-deep .el-table td,
.scale-list-table ::v-deep .el-table th.is-leaf {
  border-right-color: transparent !important;
}
.scale-list-table ::v-deep .el-table__body tr.scale-row--inactive > td {
  color: var(--text-secondary) !important;
  background-color: rgba(144, 147, 153, 0.06) !important;
}
.scale-list-table ::v-deep .el-table__body tr.scale-row--inactive:hover > td {
  background-color: rgba(144, 147, 153, 0.09) !important;
}
.scale-list-table ::v-deep .el-table__body tr.current-row > td {
  background-color: rgba(64, 158, 255, 0.14) !important;
  color: var(--text-primary) !important;
  font-weight: 700;
  box-shadow: inset 0 1px 0 rgba(64, 158, 255, 0.35), inset 0 -1px 0 rgba(64, 158, 255, 0.35);
}
.scale-list-table ::v-deep .el-table__body tr.current-row.scale-row--inactive > td {
  background-color: rgba(144, 147, 153, 0.12) !important;
  box-shadow: inset 0 1px 0 rgba(144, 147, 153, 0.35), inset 0 -1px 0 rgba(144, 147, 153, 0.35);
}
.scale-list-table ::v-deep .el-table__body tr.current-row > td:first-child {
  border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;
}
.scale-list-table ::v-deep .el-table__body tr.current-row > td:last-child {
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
}
.scale-list-table ::v-deep .el-table__body tr.current-row .el-tag {
  border-color: rgba(64, 158, 255, 0.55) !important;
  background-color: rgba(64, 158, 255, 0.12) !important;
}
.scale-list-table ::v-deep .el-table__body tr.current-row.scale-row--inactive .el-tag {
  border-color: rgba(144, 147, 153, 0.55) !important;
  background-color: rgba(144, 147, 153, 0.12) !important;
}
.scale-list-table ::v-deep .el-table__body tr.scale-row--inactive,
.scale-list-table ::v-deep .el-table__body tr.scale-row--inactive > td {
  cursor: not-allowed;
}
.pager {
  margin-top: 10px;
  display: flex;
  justify-content: center;
}
.sub-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
}
.hint {
  margin-top: 6px;
  color: var(--text-secondary);
  font-size: 12px;
}
.actions {
  display: flex;
  gap: 10px;
}
.empty {
  height: 620px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.empty-title {
  color: var(--text-secondary);
  font-weight: 700;
}
.inline-section {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--border-color);
}
.inline-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 800;
  margin-bottom: 8px;
}
.inline-actions {
  display: flex;
  gap: 8px;
}
.question-actions {
  margin-top: 8px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.sortable-ghost {
  opacity: 0.5;
  background: #f0f9eb;
}
</style>

<style>
.question-selector-dialog .question-selector__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}
.question-selector-dialog .question-selector__search {
  width: 320px;
}
.question-selector-dialog .question-selector__meta {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--text-secondary);
  font-size: 12px;
}
.question-selector-dialog .question-selector__hint {
  color: var(--text-secondary);
}
.question-selector-dialog .el-table__body tr.question-selector__row--selected > td {
  background-color: rgba(64, 158, 255, 0.12) !important;
}
.dark .question-selector-dialog .el-table__body tr.question-selector__row--selected > td {
  background-color: rgba(64, 158, 255, 0.2) !important;
}
</style>
